import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle, Phone } from "@phosphor-icons/react";
import ServicesMarquee from "@/components/ServicesMarquee";
import { SERVICES, COMPANY, TESTIMONIALS } from "@/lib/siteData";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_fitzgerald-earth/artifacts/a1re4018_50739454_1340517442756253_5511227533724483584_n.jpg";

const FEATURE_IMG =
  "https://customer-assets.emergentagent.com/job_fitzgerald-earth/artifacts/wvhqfu3p_484179611_1145985267316991_6401744673903490293_n.jpg";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex flex-col justify-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Fitzgerald Earthworks Ltd Volvo tipper truck"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/30" />
        </div>

        <div className="relative container-fz pt-32 pb-16 md:pb-24 text-[#F6F5F2]">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs md:text-sm tracking-[0.28em] uppercase font-bold text-[#F6F5F2]/80 mb-6"
          >
            Earthmoving Contractor — Mangatangi, NZ
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] max-w-5xl"
          >
            Practical earthworks,<br />
            <span className="text-[#D05322]">done right.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-8 max-w-xl text-base md:text-lg text-stone-200/90 leading-relaxed"
          >
            Family-owned excavation and site preparation across the Waikato
            and Auckland regions. Reliable workmanship, modern equipment,
            and 20+ years getting the job done.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/contact" data-testid="hero-cta-quote" className="btn-primary">
              Get a Free Quote <ArrowRight size={16} weight="bold" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-services"
              className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-[#F6F5F2] hover:text-stone-900 text-[#F6F5F2] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.18em] border border-[#F6F5F2] transition-all duration-200"
            >
              Our Services <ArrowUpRight size={16} weight="bold" />
            </Link>
          </motion.div>

          {/* Hero stat strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#F6F5F2]/15 border border-[#F6F5F2]/15 max-w-4xl">
            {[
              { v: "20+", l: "Years in industry" },
              { v: "10", l: "Specialist services" },
              { v: "100%", l: "Family operated" },
              { v: "3", l: "Regions served" },
            ].map((s, i) => (
              <div
                key={i}
                className="bg-black/30 backdrop-blur-sm px-5 py-6"
              >
                <p className="font-display text-3xl md:text-4xl text-[#F6F5F2]">
                  {s.v}
                </p>
                <p className="mt-2 text-[11px] tracking-[0.22em] uppercase text-stone-300">
                  {s.l}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServicesMarquee variant="dark" />

      {/* INTRO */}
      <section className="section-pad bg-[#F6F5F2]">
        <div className="container-fz grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-6">— 01 / Who we are</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-stone-900 leading-[1.05]">
              A Mangatangi family business, shaping the land for over twenty years.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-2">
            <p className="text-base md:text-lg text-stone-700 leading-relaxed">
              {COMPANY.name} is a family-owned earthmoving contractor based in
              Mangatangi, serving the wider Waikato, Auckland and Hauraki regions. With
              years of hands-on experience in rural, agricultural and residential
              earthworks, we deliver reliable excavation, site preparation,
              drainage and land development solutions.
            </p>
            <p className="mt-5 text-base md:text-lg text-stone-700 leading-relaxed">
              Our focus is straightforward: quality workmanship, practical
              solutions, and getting the job done right — the first time.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/about" data-testid="home-about-link" className="btn-ghost">
                About the business <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="border-t border-[#D2D0C9] bg-[#F6F5F2]">
        <div className="container-fz py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <p className="eyebrow mb-6">— 02 / What we do</p>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-stone-900 max-w-2xl leading-tight">
                Ten specialist services, one reliable crew.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="home-services-all-link"
              className="text-sm font-bold tracking-[0.2em] uppercase text-stone-900 hover:text-[#D05322] inline-flex items-center gap-2"
            >
              View all services <ArrowRight size={16} weight="bold" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D2D0C9] border border-[#D2D0C9]">
            {SERVICES.map((s, i) => (
              <Link
                key={s.slug}
                to="/services"
                data-testid={`home-service-card-${s.slug}`}
                className="group bg-[#F6F5F2] hover:bg-white p-8 md:p-10 transition-colors duration-300 flex flex-col"
              >
                <span className="num-marker">0{i + 1}</span>
                <h3 className="mt-5 font-display text-2xl text-stone-900 group-hover:text-[#D05322] transition-colors leading-tight">
                  {s.title}
                </h3>
                <p className="mt-4 text-stone-600 leading-relaxed text-sm md:text-base flex-1">
                  {s.summary}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold tracking-[0.22em] uppercase text-stone-900 group-hover:text-[#D05322]">
                  Learn more <ArrowUpRight size={14} weight="bold" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section-pad bg-[#1A1A18] text-[#F6F5F2]">
        <div className="container-fz grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img
              src={FEATURE_IMG}
              alt="Fitzgerald Earthworks Ltd Hitachi Zaxis 130 excavator on site"
              className="w-full aspect-[4/5] object-cover border border-stone-800"
            />
          </div>
          <div className="lg:col-span-7">
            <p className="eyebrow text-stone-500 mb-6">— 03 / Why Fitzgerald</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.05] max-w-2xl">
              Local knowledge.
              <br />
              Modern equipment.
              <br />
              <span className="text-[#D05322]">Honest work.</span>
            </h2>

            <ul className="mt-10 space-y-5">
              {[
                "Family-owned and operated — you deal directly with the people doing the work",
                "Modern fleet maintained on-site and ready when you are",
                "Plain-English quotes with no surprises at invoice time",
                "Rural, agricultural and residential — we've seen most ground in the region",
                "Strong focus on site tidiness, health & safety, and finishing well",
              ].map((point, i) => (
                <li
                  key={i}
                  className="flex items-start gap-4 border-b border-stone-800 pb-5"
                >
                  <CheckCircle
                    weight="duotone"
                    size={26}
                    className="text-[#D05322] shrink-0 mt-0.5"
                  />
                  <span className="text-stone-200 leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/equipment" data-testid="home-equipment-link" className="btn-primary">
                See the fleet <ArrowRight size={16} weight="bold" />
              </Link>
              <a href={COMPANY.phoneHref} data-testid="home-phone-link"
                className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-[#F6F5F2] hover:text-stone-900 text-[#F6F5F2] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.18em] border border-[#F6F5F2] transition-all duration-200">
                <Phone size={16} weight="bold" /> {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL TEASER */}
      <section className="section-pad bg-[#F6F5F2] border-t border-[#D2D0C9]">
        <div className="container-fz">
          <p className="eyebrow mb-6">— 04 / Word on the ground</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {TESTIMONIALS.slice(0, 2).map((t, i) => (
              <figure
                key={i}
                className="border border-[#D2D0C9] bg-white p-8 md:p-10 hover:border-stone-900 transition-colors"
              >
                <blockquote className="font-display text-xl md:text-2xl leading-snug text-stone-900">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-6 text-sm">
                  <span className="font-bold text-stone-900">{t.name}</span>
                  <span className="block text-stone-500 mt-1">{t.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
          <div className="mt-10">
            <Link
              to="/testimonials"
              data-testid="home-testimonials-link"
              className="text-sm font-bold tracking-[0.2em] uppercase text-stone-900 hover:text-[#D05322] inline-flex items-center gap-2"
            >
              Read more testimonials <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#D05322] text-[#F6F5F2]">
        <div className="container-fz py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <h2 className="md:col-span-8 font-display text-3xl md:text-4xl lg:text-5xl leading-[1.05]">
            Got a job lined up?
            <br />
            Let's talk through it.
          </h2>
          <div className="md:col-span-4 flex flex-col sm:flex-row md:justify-end gap-4">
            <Link
              to="/contact"
              data-testid="cta-bottom-quote"
              className="inline-flex items-center justify-center gap-2 bg-[#1A1A18] hover:bg-stone-900 text-[#F6F5F2] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.18em] border border-[#1A1A18] transition-all"
            >
              Request a Quote <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
